import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import PromotionsEvolution from './components/biReports/PromotionsEvolution';
import PromotionsPerformanceByPromotion from './components/biReports/PromotionsPerformanceByPromotion';
import PromotionsOpenClaimsByPromotion from './components/biReports/PromotionsOpenClaimsByPromotion';
import PromotionsOrdersByLocation from './components/biReports/PromotionsOrdersByLocation';
import PromotionsDetailedExport from './components/biReports/PromotionsDetailedExport';
import PromotionKpiCard from './components/biReports/PromotionKpiCard';
import BackofficeReportsApi from './components/biReports/backofficeReportsApi';

import Client from './components/Client';
import CustomPeriodPopover, { businessTodayIso, validateCustomPeriod } from './components/biReports/CustomPeriodPopover';

const REPORT_KEYS = ['kpi', 'evolution', 'performance', 'openClaims', 'locations'];

function emptyReportState() {
  return { data: null, loading: true, error: '', updatedAt: null };
}

function reportErrorMessage(error) {
  const status = Number(error && error.status);

  if (error && error.code === 'client_county_filter_not_applied') {
    return 'The backend did not apply the selected county. Retry after the backend update.';
  }
  if (error && error.code === 'promotion_filter_not_applied') {
    return 'The backend did not apply the selected promotion. Retry after the backend update.';
  }
  if (status === 401) return 'Your session has expired. Sign in again and retry.';
  if (status === 403) return 'You do not have permission to view this promotions report.';
  if (status === 404) return 'This promotions report is not available on the backend.';
  if (status === 400) return 'The selected report period is not valid.';
  if (status >= 500) return 'The server could not load this report. Please retry.';
  return 'The report could not be loaded. Check the connection and retry.';
}

function periodLabel(periodMode) {
  if (periodMode === 'custom') return 'the selected period';
  if (periodMode === 'today') return 'today (in progress)';
  if (periodMode === 'yesterday') return 'yesterday';
  if (periodMode === 'last_7d') return 'last 7 days';
  if (periodMode === 'last_90d') return 'last 90 days';
  return 'last 30 days';
}

function compactPeriodLabel(periodMode) {
  if (periodMode === 'custom') return 'custom';
  if (periodMode === 'today') return 'today';
  if (periodMode === 'yesterday') return 'yday';
  if (periodMode === 'last_7d') return '7d';
  if (periodMode === 'last_90d') return '90d';
  return '30d';
}

function compactAmount(value) {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

function formatPeriodRange(period) {
  if (!period?.dateFrom || !period?.dateTo) return '';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const from = new Date(`${period.dateFrom}T12:00:00`).toLocaleDateString('ro-RO', options);
  const to = new Date(`${period.dateTo}T12:00:00`).toLocaleDateString('ro-RO', options);
  return `${from} – ${to}`;
}

function formatUpdateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleTimeString('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest',
  });
}

function promotionFilterOptions(rows) {
  const promotions = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const promotionIref = Number(row && row.promotion_iref);
    if (!Number.isInteger(promotionIref) || promotionIref < 1) return;

    const name = String(row.promotion_name || '').trim() || 'Unnamed promotion';
    promotions.set(String(promotionIref), {
      value: String(promotionIref),
      label: `${name} (#${promotionIref})`,
      name,
    });
  });

  return [
    { value: 'all', label: 'All promotions', name: 'All promotions' },
    ...Array.from(promotions.values()).sort((left, right) => (
      left.label.localeCompare(right.label, 'ro', { sensitivity: 'base' })
    )),
  ];
}

class RapPromotions extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      periodMode: "last_30d",
      dateFrom: '',
      dateTo: '',
      customPeriodOpen: false,
      draftDateFrom: '',
      draftDateTo: '',
      customPeriodError: '',
      countyFilter: "all",
      promotionFilter: "all",
      locationPromotionFilter: "all",
      countyOptions: [{ value: "all", label: "All counties" }],
      reports: {
        kpi: emptyReportState(),
        evolution: emptyReportState(),
        performance: emptyReportState(),
        openClaims: emptyReportState(),
        locations: emptyReportState(),
      },
    };
    this.isMountedFlag = false;
    this.reportRequestIds = {};
    this.customPeriodButton = React.createRef();
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.loadReports(this.state.periodMode, this.state.countyFilter);
    if (this.props.onReady) {
      this.props.onReady();
    }
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    REPORT_KEYS.forEach((key) => {
      this.reportRequestIds[key] = (this.reportRequestIds[key] || 0) + 1;
    });
  }

  getReportRequest = (
    key,
    periodMode,
    countyFilter,
    promotionFilter,
  ) => {
    const query = {
      periodMode,
      county: countyFilter,
      ...(periodMode === 'custom' ? { dateFrom: this.state.dateFrom, dateTo: this.state.dateTo } : {}),
    };
    const client = this.state.client;
    const reportPromotionFilter = promotionFilter === undefined
      ? (key === 'locations' ? this.state.locationPromotionFilter : this.state.promotionFilter)
      : promotionFilter;

    if (key === 'kpi') {
      if (this.state.countyOptions.length <= 1) query.includeCountyOptions = true;
      return BackofficeReportsApi.getPromotionsKpiSummaryAsync(client, query);
    }
    if (key === 'evolution' || key === 'locations') {
      if (reportPromotionFilter !== 'all') query.promotionIref = reportPromotionFilter;
    }
    if (key === 'evolution') {
      return BackofficeReportsApi.getPromotionsActivityEvolutionAsync(client, query);
    }
    if (key === 'performance') return BackofficeReportsApi.getPromotionsPerformanceAsync(client, query);
    if (key === 'openClaims') return BackofficeReportsApi.getPromotionsOpenClaimsAsync(client, query);
    return BackofficeReportsApi.getPromotionsOrdersByLocationAsync(client, query);
  };

  loadReport = (
    key,
    periodMode = this.state.periodMode,
    countyFilter = this.state.countyFilter,
    promotionFilter,
  ) => {
    const reportPromotionFilter = promotionFilter === undefined
      ? (key === 'locations' ? this.state.locationPromotionFilter : this.state.promotionFilter)
      : promotionFilter;
    const requestId = (this.reportRequestIds[key] || 0) + 1;
    this.reportRequestIds[key] = requestId;

    this.setState((state) => ({
      reports: {
        ...state.reports,
        [key]: { ...state.reports[key], loading: true, error: '' },
      },
    }));

    let request;
    try {
      request = this.getReportRequest(
        key,
        periodMode,
        countyFilter,
        reportPromotionFilter,
      );
    } catch (error) {
      request = Promise.reject(error);
    }

    Promise.resolve(request).then((data) => {
      if (!this.isMountedFlag || this.reportRequestIds[key] !== requestId) return;
      if (
        countyFilter !== 'all'
        && String(
          key === 'locations'
            ? (data?.meta?.locationCounty || data?.meta?.clientCounty || '')
            : (data?.meta?.clientCounty || ''),
        ) !== String(countyFilter)
      ) {
        const unsupportedFilterError = new Error('Client county filter was not applied.');
        unsupportedFilterError.code = 'client_county_filter_not_applied';
        throw unsupportedFilterError;
      }
      if (
        (key === 'evolution' || key === 'locations')
        && reportPromotionFilter !== 'all'
        && String(data?.meta?.promotionIref || '') !== String(reportPromotionFilter)
      ) {
        const unsupportedFilterError = new Error('Promotion filter was not applied.');
        unsupportedFilterError.code = 'promotion_filter_not_applied';
        throw unsupportedFilterError;
      }
      this.setState((state) => ({
        countyOptions: (
          key === 'kpi'
          && Array.isArray(data?.county_options)
          && data.county_options.length > 0
        ) ? data.county_options : state.countyOptions,
        reports: {
          ...state.reports,
          [key]: {
            data: data || {},
            loading: false,
            error: '',
            updatedAt: data?.generated_at || data?.generatedAt || new Date().toISOString(),
          },
        },
      }));
    }).catch((error) => {
      if (!this.isMountedFlag || this.reportRequestIds[key] !== requestId) return;
      this.setState((state) => ({
        reports: {
          ...state.reports,
          [key]: {
            ...state.reports[key],
            loading: false,
            error: reportErrorMessage(error),
          },
        },
      }));
    });
  };

  loadReports = (
    periodMode = this.state.periodMode,
    countyFilter = this.state.countyFilter,
  ) => {
    REPORT_KEYS.forEach((key) => this.loadReport(key, periodMode, countyFilter));
  };

  handlePeriodChange = (event, newPeriodMode) => {
    if (newPeriodMode === 'custom') {
      const meta = this.state.reports.kpi.data?.meta;
      this.setState({
        customPeriodOpen: true,
        draftDateFrom: this.state.dateFrom || meta?.dateFrom || businessTodayIso(),
        draftDateTo: this.state.dateTo || meta?.dateTo || businessTodayIso(),
        customPeriodError: '',
      });
      return;
    }
    if (!newPeriodMode || newPeriodMode === this.state.periodMode) return;

    this.setState({
      periodMode: newPeriodMode,
      promotionFilter: 'all',
      locationPromotionFilter: 'all',
      reports: Object.fromEntries(REPORT_KEYS.map((key) => [key, emptyReportState()])),
    }, () => {
      this.loadReports(newPeriodMode, this.state.countyFilter);
    });
  };

  handleCustomPeriodApply = () => {
    const { draftDateFrom, draftDateTo } = this.state;
    const customPeriodError = validateCustomPeriod(draftDateFrom, draftDateTo);
    if (customPeriodError) {
      this.setState({ customPeriodError });
      return;
    }
    if (this.state.periodMode === 'custom' && this.state.dateFrom === draftDateFrom && this.state.dateTo === draftDateTo) {
      this.setState({ customPeriodOpen: false });
      return;
    }
    this.setState({
      periodMode: 'custom',
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      customPeriodOpen: false,
      promotionFilter: 'all',
      locationPromotionFilter: 'all',
      reports: Object.fromEntries(REPORT_KEYS.map((key) => [key, emptyReportState()])),
    }, () => this.loadReports());
  };

  handleCountyChange = (event, option) => {
    const countyFilter = String(option?.value || 'all');
    if (countyFilter === this.state.countyFilter) return;

    this.setState({
      countyFilter,
      promotionFilter: 'all',
      locationPromotionFilter: 'all',
    }, () => {
      this.loadReports(this.state.periodMode, countyFilter);
    });
  };

  handlePromotionChange = (event, option) => {
    const promotionFilter = String(option?.value || 'all');
    if (promotionFilter === this.state.promotionFilter) return;

    this.setState({ promotionFilter }, () => {
      this.loadReport(
        'evolution',
        this.state.periodMode,
        this.state.countyFilter,
        promotionFilter,
      );
    });
  };

  handleLocationPromotionChange = (event, option) => {
    const locationPromotionFilter = String(option?.value || 'all');
    if (locationPromotionFilter === this.state.locationPromotionFilter) return;

    this.setState({ locationPromotionFilter }, () => {
      this.loadReport(
        'locations',
        this.state.periodMode,
        this.state.countyFilter,
        locationPromotionFilter,
      );
    });
  };

  renderReportState = (key, content) => {
    const report = this.state.reports[key] || emptyReportState();
    const hasPreviousData = report.data !== null;

    if (report.loading && !hasPreviousData) {
      return (
        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: '100%', p: 2 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">Loading report…</Typography>
        </Stack>
      );
    }

    if (report.error) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 2 }}>
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={() => this.loadReport(key)}>
                Retry
              </Button>
            )}
          >
            {report.error}
          </Alert>
        </Stack>
      );
    }

    return (
      <Stack spacing={0.5} sx={{ height: '100%', minHeight: 0 }} aria-busy={report.loading}>
        {report.loading && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.75}
            role="status"
            sx={{
              flexShrink: 0,
              py: 0.35,
              color: '#475569',
              bgcolor: '#f8fafc',
              borderRadius: '8px',
            }}
          >
            <CircularProgress size={14} />
            <Typography variant="caption">Refreshing…</Typography>
          </Stack>
        )}

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            pointerEvents: report.loading ? 'none' : 'auto',
            '& > *': { height: '100%' },
          }}
        >
          {content}
        </Box>
      </Stack>
    );
  };

  render() {
    const {
      periodMode,
      countyFilter,
      promotionFilter,
      locationPromotionFilter,
      countyOptions,
      reports,
      client,
    } = this.state;
    const kpiReport = reports.kpi || emptyReportState();
    const hasPreviousKpiData = kpiReport.data !== null;
    const reportsRefreshing = REPORT_KEYS.some((key) => reports[key] && reports[key].loading);
    const selectedCounty = countyOptions.find(
      (option) => String(option.value) === String(countyFilter),
    ) || { value: countyFilter, label: countyFilter };
    const promotionOptions = promotionFilterOptions(
      reports.performance.data && reports.performance.data.rows,
    );
    const selectedPromotion = promotionOptions.find(
      (option) => String(option.value) === String(promotionFilter),
    ) || promotionOptions[0];
    const selectedLocationPromotion = promotionOptions.find(
      (option) => String(option.value) === String(locationPromotionFilter),
    ) || promotionOptions[0];
    const kpiData = kpiReport.data || {};
    const kpiMeta = kpiData.meta || {};
    const rangeLabel = formatPeriodRange(kpiMeta);
    const definitions = kpiMeta.definitions || {};
    const issuedClaimsConsumed = kpiData.issued_claims_consumed_by_period_end !== undefined
      ? kpiData.issued_claims_consumed_by_period_end
      : kpiData.claims_created_consumed;
    const issuedConversion = kpiData.issued_claim_conversion !== undefined
      ? kpiData.issued_claim_conversion
      : kpiData.claim_conversion;
    const issuedConversionChange = kpiData.issued_claim_conversion_change !== undefined
      ? kpiData.issued_claim_conversion_change
      : kpiData.claim_conversion_change;
    const discountRate = Number(kpiData.discount_rate || 0);
    const registeredClientSales = Number(kpiData.registered_client_sales);
    const promoSalesShare = Number(kpiData.promo_sales_share_of_registered);
    const hasRegisteredSalesTotal = (
      kpiData.registered_client_sales !== null
      && kpiData.registered_client_sales !== undefined
      && Number.isFinite(registeredClientSales)
    );
    const hasRegisteredSalesComparison = (
      hasRegisteredSalesTotal
      && kpiData.promo_sales_share_of_registered !== null
      && kpiData.promo_sales_share_of_registered !== undefined
      && Number.isFinite(promoSalesShare)
    );
    const currency = kpiMeta.currency || 'RON';
    const hasKpiActivity = [
      kpiData.claims_created,
      kpiData.claims_consumed,
      kpiData.orders_with_claims,
      kpiData.sales_from_claims,
      kpiData.open_claims,
    ].some((value) => Number(value || 0) !== 0);
    const comparisonEnabled = periodMode !== 'today';
    const comparisonValue = (value) => (comparisonEnabled ? value : undefined);
    const promoShareCard = {
      title: 'Promo share',
      value: promoSalesShare,
      valueLabel: hasRegisteredSalesComparison ? undefined : 'N/A',
      isPercent: true,
      badge: hasRegisteredSalesTotal
        ? `${compactAmount(registeredClientSales)} ${currency} reg. sales`
        : 'Registered sales unavailable',
      mobileBadge: hasRegisteredSalesTotal
        ? `${compactAmount(registeredClientSales)} ${currency} reg.`
        : 'Sales unavailable',
      color: '#00695c',
      titleTooltip: hasRegisteredSalesComparison
        ? `${registeredClientSales.toFixed(2)} ${currency} in registered-client sales; net promotion-linked sales represent ${promoSalesShare.toFixed(1)}% for the same period and filters.`
        : hasRegisteredSalesTotal
          ? 'Registered-client sales are zero for the same period and filters, so the promo share is unavailable.'
          : 'Registered-client sales comparison is unavailable because the response did not include it.',
    };

    const kpiCards = [
      {
        title: 'Claims issued',
        value: kpiData.claims_created,
        badge: `${issuedClaimsConsumed || 0} used by period end`,
        mobileBadge: `${issuedClaimsConsumed || 0} used`,
        change: comparisonValue(kpiData.claims_created_change),
        titleTooltip: definitions.claims_created,
      },
      {
        title: 'Claims used',
        value: kpiData.claims_consumed,
        badge: 'used in selected period',
        mobileBadge: 'in period',
        change: comparisonValue(kpiData.claims_consumed_change),
        titleTooltip: definitions.claims_consumed,
      },
      {
        title: 'Issued conversion',
        value: issuedConversion,
        badge: `${issuedClaimsConsumed || 0}/${kpiData.claims_created || 0} by period end`,
        mobileBadge: `${issuedClaimsConsumed || 0}/${kpiData.claims_created || 0} used`,
        change: comparisonValue(issuedConversionChange),
        titleTooltip: definitions.issued_claim_conversion,
        isPercent: true,
      },
      {
        title: 'Promo orders',
        value: kpiData.orders_with_claims,
        badge: `${kpiData.claims_consumed || 0} claims used`,
        mobileBadge: `${kpiData.claims_consumed || 0} claims`,
        change: comparisonValue(kpiData.orders_with_claims_change),
      },
      promoShareCard,
      {
        title: 'Net promo sales',
        value: kpiData.sales_from_claims,
        badge: `${kpiData.orders_with_claims || 0} orders`,
        mobileBadge: `${kpiData.orders_with_claims || 0} orders`,
        change: comparisonValue(kpiData.sales_from_claims_change),
        titleTooltip: definitions.sales_from_claims,
      },
      {
        title: 'Promo discount',
        value: kpiData.discount_from_claims,
        badge: `${discountRate.toFixed(1)}% of gross promo sales`,
        mobileBadge: `${discountRate.toFixed(1)}% gross`,
        change: comparisonValue(kpiData.discount_from_claims_change),
        changeDirection: 'lower_is_better',
        color: discountRate > 15 ? '#b91c1c' : '#1a237e',
        titleTooltip: 'Promotion discount divided by gross promotion-linked sales. Gross sales equal net promo sales plus promotion discount. Rates above 15% are highlighted red in detailed tables.',
      },
      {
        title: 'Avg. discount',
        value: kpiData.average_discount,
        valueLabel:
          kpiData.average_discount != null
            ? Number(kpiData.average_discount).toFixed(2)
            : "-",
        badge: `${discountRate.toFixed(1)}% gross discount rate`,
        mobileBadge: `${discountRate.toFixed(1)}% gross`,
        change: comparisonValue(kpiData.average_discount_change),
        changeDirection: 'lower_is_better',
        titleTooltip: 'Average discount per promotion order.',
      },
    ];

    const fullWidthChildSx = {
      width: '100%',
      minWidth: 0,
      height: '100%',
      display: 'flex',
      '& > *': {
        width: '100% !important',
        maxWidth: '100% !important',
        height: '100% !important',
        mx: '0 !important',
        boxSizing: 'border-box',
      },
    };

    const reportBoxSx = {
      width: '100%',
      minWidth: 0,
      height: {
        xs: 380,
        sm: 420,
        md: 460,
        lg: 500,
      },
      minHeight: 0,
      overflow: 'hidden',
      borderRadius: {
        xs: '14px',
        sm: '16px',
      },
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
      border: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: '#fff',
      boxSizing: 'border-box',

      '& > *': {
        width: '100% !important',
        maxWidth: '100% !important',
        height: '100% !important',
        maxHeight: '100% !important',
        minHeight: '0 !important',
        mx: '0 !important',
        boxSizing: 'border-box',
      },

      '& .MuiCard-root': {
        width: '100% !important',
        height: '100% !important',
        maxHeight: '100% !important',
        minHeight: '0 !important',
        borderRadius: {
          xs: '14px',
          sm: '16px',
        },
        boxShadow: 'none',
        border: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
      },

      '& .MuiCardContent-root': {
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
      },
    };

    return (
      <Box
        component="main"
        sx={{
          // bgcolor: '#f4f7fb',
          minHeight: '100vh',
          flexGrow: 1,
          width: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          maxWidth: {
            xs: 'calc(100vw - 8px)',
            sm: '100%',
          },
          ml: {
            xs: 'calc(50% - 50vw + 4px)',
            sm: 0,
          },
          overflowX: 'hidden',
          pt: { xs: 0.75, sm: 1.5, md: 3 },
          pb: { xs: 1, sm: 2, md: 3 },
          px: { xs: 0.5, sm: 1, md: 2 },
          boxSizing: 'border-box',

          '& .MuiGrid-item': {
            minWidth: 0,
          },
          '& .MuiCard-root': {
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        <Grid
          container
          spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
          sx={{
            width: '100%',
            maxWidth: '100%',
            m: 0,
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              p: '0 !important',
              mb: { xs: 0.75, md: 1.25 },
            }}
          >
            <Stack
              spacing={{ xs: 1, sm: 1.25 }}
              sx={{
                width: '100%',
                px: { xs: 1.25, sm: 1.75, md: 2 },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                bgcolor: '#ffffff',
                borderRadius: { xs: '14px', sm: '16px' },
                border: '1px solid rgba(15, 23, 42, 0.06)',
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                boxSizing: 'border-box',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' },
                      lineHeight: 1.2,
                      color: '#020617',
                    }}
                  >
                    Promotions
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.25,
                      color: '#475569',
                      fontSize: { xs: '0.72rem', sm: '0.82rem' },
                    }}
                  >
                    Promo claims, usage, sales impact and discount cost
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={periodMode}
                    onChange={this.handlePeriodChange}
                    aria-label="Promotions period"
                    disabled={reportsRefreshing}
                    sx={{
                      alignSelf: 'center',
                      flexShrink: 0,
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                      '& .MuiToggleButtonGroup-grouped': {
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                      },
                      '& .MuiToggleButton-root': {
                        minWidth: { xs: 34, sm: 42 },
                        px: { xs: 0.75, sm: 1.25 },
                        py: { xs: 0.35, sm: 0.45 },
                        fontSize: { xs: '0.66rem', sm: '0.75rem' },
                        fontWeight: 700,
                        textTransform: 'none',
                        color: '#334155',
                        borderColor: 'rgba(15, 23, 42, 0.08)',
                        '&.Mui-selected': {
                          bgcolor: '#0f172a',
                          color: '#fff',
                          '&:hover': {
                            bgcolor: '#111827',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="today" aria-label="Today" title="Today (in progress)">Today</ToggleButton>
                    <ToggleButton value="yesterday" aria-label="Yesterday" title="Yesterday">YDay</ToggleButton>
                    <ToggleButton value="last_7d">7d</ToggleButton>
                    <ToggleButton value="last_30d">30d</ToggleButton>
                    <ToggleButton value="last_90d">90d</ToggleButton>
                    <ToggleButton
                      ref={this.customPeriodButton}
                      value="custom"
                      aria-haspopup="dialog"
                      aria-expanded={this.state.customPeriodOpen}
                      aria-controls={this.state.customPeriodOpen ? 'promotions-custom-period' : undefined}
                      onClick={periodMode === 'custom' ? (event) => this.handlePeriodChange(event, 'custom') : undefined}
                    >
                      Custom
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>

              <CustomPeriodPopover
                id="promotions-custom-period"
                open={this.state.customPeriodOpen}
                anchorEl={this.customPeriodButton.current}
                onClose={() => this.setState({ customPeriodOpen: false })}
                dateFrom={this.state.draftDateFrom}
                dateTo={this.state.draftDateTo}
                onDateFromChange={(value) => this.setState({ draftDateFrom: value, customPeriodError: '' })}
                onDateToChange={(value) => this.setState({ draftDateTo: value, customPeriodError: '' })}
                onApply={this.handleCustomPeriodApply}
                error={this.state.customPeriodError}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.65, sm: 1 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box minWidth={0} sx={{ alignSelf: 'flex-start', textAlign: 'left' }}>
                  {rangeLabel && (
                    <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 650 }}>
                      {rangeLabel} · {selectedCounty.label}{periodMode === 'today' ? ' · In progress' : ''}
                    </Typography>
                  )}
                  {kpiReport.updatedAt && (
                    <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.68rem', lineHeight: 1.35 }}>
                      Updated {formatUpdateTime(kpiReport.updatedAt)}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end' }}>
                  <PromotionsDetailedExport
                    client={client}
                    periodMode={periodMode}
                    dateFrom={periodMode === 'custom' ? this.state.dateFrom : kpiMeta.dateFrom}
                    dateTo={periodMode === 'custom' ? this.state.dateTo : kpiMeta.dateTo}
                    county={countyFilter}
                    countyName={selectedCounty.label}
                  />
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                  width: '100%',
                  p: { xs: 1, sm: 1.15 },
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxSizing: 'border-box',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '10px',
                      bgcolor: 'rgba(26, 35, 126, 0.10)',
                      color: '#1a237e',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocationOnRoundedIcon sx={{ fontSize: 19 }} />
                  </Box>
                  <Box minWidth={0}>
                    <Typography sx={{ color: '#0f172a', fontSize: '0.78rem', fontWeight: 800 }}>
                      County scope
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.7rem', lineHeight: 1.25 }}>
                      Client county for promotion metrics; POS county for orders by location.
                    </Typography>
                  </Box>
                </Stack>

                <Autocomplete
                  size="small"
                  disableClearable={countyFilter === 'all'}
                  clearText="Reset to all counties"
                  autoHighlight
                  loading={reports.kpi.loading && countyOptions.length <= 1}
                  disabled={reportsRefreshing}
                  options={countyOptions}
                  value={selectedCounty}
                  onChange={this.handleCountyChange}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search or select county"
                      inputProps={{ ...params.inputProps, 'aria-label': 'County scope' }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnRoundedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    width: { xs: '100%', sm: 260, md: 290 },
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      borderRadius: '10px',
                      fontSize: { xs: '16px', sm: '0.8rem' },
                      fontWeight: 650,
                    },
                    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                      fontSize: { xs: '16px', sm: '0.8rem' },
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Grid>

          {kpiReport.error && (
            <Grid item xs={12} sx={{ p: '0 !important' }}>
              <Alert
                severity="error"
                action={(
                  <Button color="inherit" size="small" onClick={() => this.loadReport('kpi')}>
                    Retry
                  </Button>
                )}
              >
                {kpiReport.error}
              </Alert>
            </Grid>
          )}

          {!kpiReport.loading && !kpiReport.error && !hasKpiActivity && (
            <Grid item xs={12} sx={{ p: '0 !important' }}>
              <Alert severity="info">
                No promotion activity was found for {periodLabel(kpiMeta.periodMode || periodMode)}.
                {' '}The zero values below are valid.
              </Alert>
            </Grid>
          )}

          {!kpiReport.error && [kpiCards.slice(0, 4), kpiCards.slice(4)].map((cardGroup, groupIndex) => (
            <Grid
              item
              xs={12}
              key={`kpi-group-${groupIndex}`}
              sx={{
                p: '0 !important',
                mt: groupIndex === 1 ? { xs: 0.75, md: 1.5 } : 0,
              }}
            >
              <Grid
                container
                spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
                sx={{ width: '100%', m: 0 }}
              >
                {cardGroup.map((card) => (
                  <Grid
                    item
                    lg={3}
                    md={6}
                    sm={6}
                    xs={6}
                    key={card.title}
                    sx={{ display: 'flex' }}
                  >
                    <Box sx={fullWidthChildSx}>
                      <PromotionKpiCard
                        {...card}
                        caption={periodLabel(kpiMeta.periodMode || periodMode)}
                        mobileCaption={compactPeriodLabel(kpiMeta.periodMode || periodMode)}
                        mobileChangeLabel="vs prev."
                        loading={kpiReport.loading && !hasPreviousKpiData}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ))}

          <Grid
            item
            xs={12}
            md={12}
            lg={6}
            sx={{
              pl: { xs: '0 !important', md: undefined },
              mt: { xs: 0.75, md: 1.5 },
            }}
          >
            <Box
              sx={{
                ...reportBoxSx,
                height: {
                  xs: 480,
                  sm: 420,
                  md: 460,
                  lg: 500,
                },
              }}
            >
              {this.renderReportState(
                'evolution',
                <PromotionsEvolution
                  periodMode={periodMode}
                  data={(reports.evolution.data && reports.evolution.data.rows) || []}
                  meta={(reports.evolution.data && reports.evolution.data.meta) || {}}
                  promotionOptions={promotionOptions}
                  selectedPromotion={selectedPromotion}
                  promotionLoading={reports.performance.loading}
                  promotionDisabled={reports.evolution.loading || reports.performance.loading}
                  onPromotionChange={this.handlePromotionChange}
                />
              )}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={6}
            sx={{
              mt: { xs: 0.75, md: 1.5 },
            }}
          >
            <Box
              sx={{
                ...reportBoxSx,
                height: {
                  xs: 460,
                  sm: 420,
                  md: 460,
                  lg: 500,
                },
              }}
            >
              {this.renderReportState(
                'locations',
                <PromotionsOrdersByLocation
                  periodMode={periodMode}
                  data={(reports.locations.data && reports.locations.data.rows) || []}
                  meta={(reports.locations.data && reports.locations.data.meta) || {}}
                  promotionOptions={promotionOptions}
                  selectedPromotion={selectedLocationPromotion}
                  promotionLoading={reports.performance.loading}
                  promotionDisabled={reports.locations.loading || reports.performance.loading}
                  onPromotionChange={this.handleLocationPromotionChange}
                />
              )}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={6}
            sx={{
              pl: { xs: '0 !important', md: undefined },
            }}
          >
            <Box
              sx={{
                ...reportBoxSx,
                height: {
                  xs: 460,
                  sm: 500,
                  md: 540,
                  lg: 580,
                },
              }}
            >
              {this.renderReportState(
                'performance',
                <PromotionsPerformanceByPromotion
                  periodMode={periodMode}
                  data={(reports.performance.data && reports.performance.data.rows) || []}
                  meta={(reports.performance.data && reports.performance.data.meta) || {}}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            <Box
              sx={{
                ...reportBoxSx,
                height: {
                  xs: 460,
                  sm: 500,
                  md: 540,
                  lg: 580,
                },
              }}
            >
              {this.renderReportState(
                'openClaims',
                <PromotionsOpenClaimsByPromotion
                  periodMode={periodMode}
                  data={(
                    reports.openClaims.data
                    && (reports.openClaims.data.active_rows || reports.openClaims.data.rows)
                  ) || []}
                  alignedToPeriodEnd={Boolean(
                    reports.openClaims.data
                    && Array.isArray(reports.openClaims.data.active_rows)
                  )}
                  summary={(reports.openClaims.data && reports.openClaims.data.active_summary) || {}}
                  meta={(reports.openClaims.data && reports.openClaims.data.meta) || {}}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

export default RapPromotions;
